<template>
  <div class="container-fluid">
    <h1 class="mb-3">Codec Management</h1>
    
    <div v-if="auth.isAdmin" class="d-flex align-items-center mb-3">
      <AddActions view="Codec" @new="newModal"/>
      <div v-if="auth.isAdmin" class="col d-flex justify-content-end">
        <ToggleLockedButton 
          v-model="includeLocked" 
          class="me-2"
          @toggle="toggleLockedFilter"
        />
      </div>
    </div>
    <div>
      <CodecForm v-if="modalNew || modalEdit || modalDetail"
        :msg="`${modalMsg} Codec`" :tag="`codec`"
        :schema="entitySchema" :schemaTypes="schemaTypes" :parentObj="editCodec"
        @formDataSubmitted="handleSave" @formClosed="closedModal"
      />
    </div>
    <CodecTable 
      modelName="Codec"
      :columns="columns" 
      :options="tableOptions" 
      :ajaxUrl="codecEndpoint" 
      :queryParams="codecQueryParams" 
      ref="table"
    >
      <template #details="row">
        <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
      </template>
      <template #actions="row">
        <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
      </template>
    </CodecTable>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, render } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { getCommonColumns, renderTime } from '@/utils/table';
import { handleRowDelete, handleRowSave } from '@/utils/form';
import { uploadFile } from '@/utils/api';

import CodecTable from '@/components/TableComponent.vue';
import CodecForm from '@/components/DataFormModal.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';

const auth = useAuthStore();
const schema = useSchemaStore();

const table = ref();
const codecList = ref([]);
const entitySchema = ref([]);
const schemaTypes = reactive({});

const includeLocked = ref(false);

const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);
const editCodec = reactive({});

const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const columns = [
  { title: 'Customer ID', data: 'customerId', width: '15%' },
  { title: 'Codec ID', data: 'id', width: '15%', class: 'td-truncate' },
  { title: 'Type', data: 'type' },
  { title: 'File Name', data: 'fileName', defaultContent: '' },
  { title: 'Updated', data: 'updatedAt', defaultContent: '', render: renderTime },
  ...getCommonColumns(auth.isAdmin),
];

const tableOptions = computed(() => {
  return {};
});

const codecEndpoint = ApiEndpoint.CODECS;
const codecQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return params;
});

const loadCodecs = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const toggleLockedFilter = async () => {
  await loadCodecs(true);
};

const resetCodec = (obj) => {
  Object.keys(obj).forEach(key => delete obj[key]);
};

const newModal = () => {
  resetCodec(editCodec);
  modalNew.value = true;
};

const handleSave = async (detail) => {
  try {
    closedModal();
    const { codecJson, data, file, ...updates } = detail;
    if (file instanceof File && file.size > (2 * 1024 * 1024)) {
      await uploadFile(`${ApiEndpoint.CODECS}/upload`, file, updates);
    } else {
      await handleRowSave('codec', { ...updates, codecJson }, { ...editCodec });
    }
    await loadCodecs(true);
  } catch (err) {
    console.error(err);
  }
};

const edit = (row) => {
  Object.assign(editCodec, row);
  modalEdit.value = true;
};

const detail = (row) => {
  Object.assign(editCodec, row);
  modalDetail.value = true;
};

const remove = async (row) => {
  const validationKeys = ['id', 'customerId', 'fileName'];
  await handleRowDelete('codec', row, validationKeys);
  await loadCodecs(true);
}

/** Resets modal values after modal close, ready for next modal */
const closedModal = () => {
  modalNew.value = modalEdit.value = modalDetail.value = false;
};

onMounted(async() => {
  entitySchema.value = schema.getFieldMeta('codec');
  schemaTypes.value = schema.getModelEnums('codec');
});
</script>

<style scoped>
.pointer { cursor: pointer; }
.action { height: 20px; width: auto; }
</style>
